import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { AuthContext } from "../../Context/AuthContextProvider";
import TopBar from "../TopBar";
import Footerr from "../Footerr/Footerr";
import { useFilterContext } from "../../Context/filter_context";
import upi from "./payments.png";
import frameBg from "./../../Assets/Media/Frame 3.jpg";
import { post } from "../../utils/api";

// Load Cashfree SDK
const loadCashfreeSDK = () => {
  return new Promise((resolve, reject) => {
    if (window.Cashfree) {
      resolve(window.Cashfree);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    script.async = true;
    script.onload = () => resolve(window.Cashfree);
    script.onerror = () => reject(new Error("Failed to load Cashfree SDK"));
    document.body.appendChild(script);
  });
};

function MarketplaceCheckout() {
  const { filtered_products: products } = useFilterContext();
  const { isAuth, loginUser } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [isPaying, setIsPaying] = useState(false);
  const [cashfree, setCashfree] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [showOwnerWarning, setShowOwnerWarning] = useState(false);

  useEffect(() => {
    const found = products.find((p) => p._id === id);
    if (found) {
      setProduct(found);
      
      // Check if current user is the owner
      if (loginUser) {
        const userId = loginUser._id || loginUser.id;
        const userName = loginUser.name || loginUser.username;
        
        const ownerCheck = userId && (
          userId.toString() === found.submittedBy?.toString() ||
          userId.toString() === found.creator?.creatorId?.toString() ||
          userName === found.creator?.creatorName
        );
        
        setIsOwner(ownerCheck);

        if (ownerCheck) {
          setShowOwnerWarning(true);
          toast.error("You cannot purchase your own product!");
        }
      }
    } else {
      toast.error("Product not found!");
      navigate("/marketplace");
    }
  }, [products, id, loginUser, navigate]);

  // Load Cashfree SDK
  useEffect(() => {
    if (!isOwner) {
      loadCashfreeSDK()
        .then((CF) => {
          const cashfreeInstance = CF({
            mode: "production", // Change to "production" for live
          });
          setCashfree(cashfreeInstance);
        })
        .catch(() => {
          toast.error("Payment system initialization failed");
        });
    }
  }, [isOwner]);

  const handlePayment = async () => {
    // Validation
    if (!isAuth) {
      toast.error("Please login to continue.");
      navigate("/login");
      return;
    }

    // 🚫 Prevent owner from buying their own product
    if (isOwner) {
      toast.error("You cannot purchase your own product!");
      setShowOwnerWarning(true);
      return;
    }

    if (!product) {
      toast.error("Product not found!");
      return;
    }

    if (!loginUser.email_id || !loginUser.mobile_number) {
      toast.error("Please update your profile before proceeding.");
      navigate("/dashboard/Profile");
      return;
    }

    if (!cashfree) {
      toast.error("Payment system not ready. Please refresh the page.");
      return;
    }

    try {
      setIsPaying(true);
      toast.loading("Initializing payment...", { id: "payment-toast" });

      const orderData = await post("/payment/create-order", {
        productId: product._id,
        productName: product.name,
        amount: product.price,
        buyerName: loginUser.username,
        buyerEmail: loginUser.email_id,
        buyerPhone: loginUser.mobile_number,
        userId: loginUser._id,
      });

      if (!orderData.success) {
        throw new Error(orderData.error || "Failed to create payment order");
      }

      toast.dismiss("payment-toast");
      toast.loading("Opening payment gateway...", { id: "payment-toast" });

      const checkoutOptions = {
        paymentSessionId: orderData.paymentSessionId,
        returnUrl: `${window.location.origin}/payment/success?order_id=${orderData.orderId}`,
      };

      cashfree
        .checkout(checkoutOptions)
        .then(async (result) => {
          toast.dismiss("payment-toast");

          if (result.error) {
            toast.error(result.error.message || "Payment failed");
            setIsPaying(false);
            navigate(
              `/payment/success?order_id=${orderData.orderId}&status=failed`
            );
            return;
          }

          if (result.paymentDetails) {
            const paymentStatus = result.paymentDetails.payment_status;

            if (paymentStatus === "SUCCESS" || paymentStatus === "PAID") {
              toast.dismiss("payment-toast");
              navigate(
                `/payment/success?order_id=${orderData.orderId}&status=success`
              );
            } else if (paymentStatus === "FAILED") {
              toast.dismiss("payment-toast");
              toast.error("Payment failed. Please try again.");
              setIsPaying(false);
              navigate(
                `/payment/success?order_id=${orderData.orderId}&status=failed`
              );
            } else if (
              paymentStatus === "CANCELLED" ||
              paymentStatus === "USER_DROPPED"
            ) {
              toast.dismiss("payment-toast");
              toast.error("Payment cancelled");
              setIsPaying(false);
              navigate(
                `/payment/success?order_id=${orderData.orderId}&status=cancelled`
              );
            } else {
              toast.dismiss("payment-toast");
              navigate(`/payment/success?order_id=${orderData.orderId}`);
            }
          } else {
            setIsPaying(false);
          }
        })
        .catch(() => {
          toast.dismiss("payment-toast");
          toast.error("Payment system error. Please try again.");
          setIsPaying(false);
        });
    } catch (error) {
      toast.dismiss("payment-toast");
      toast.error(error.message || "Payment failed. Please try again.");
      setIsPaying(false);
    }
  };

  // Show warning message if owner tries to access checkout
  if (showOwnerWarning && product) {
    return (
      <div className="bg-[#f9f8f4] min-h-screen flex flex-col">
        <TopBar />
        <div className="flex-1 flex justify-center items-center py-16 px-4">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl shadow-2xl border border-red-200 w-full max-w-2xl p-8"
          >
            <div className="text-center">
              <div className="text-6xl mb-4">🚫</div>
              <h1 className="text-3xl font-bold text-red-600 mb-4">
                Cannot Purchase Own Product
              </h1>
              <p className="text-gray-700 mb-6">
                You are the owner of <strong>{product.name}</strong> and cannot
                purchase it yourself.
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <button
                  onClick={() => navigate("/marketplace")}
                  className="px-6 py-3 bg-gradient-to-r from-[#b99653] to-[#e3b757] text-white font-semibold rounded-lg hover:scale-105 transition"
                >
                  Browse Marketplace
                </button>
                <button
                  onClick={() => navigate("/Dashboard/MySubmissions")}
                  className="px-6 py-3 bg-[#101c4c] text-white font-semibold rounded-lg hover:scale-105 transition"
                >
                  Go to My Submissions
                </button>
              </div>
            </div>
          </motion.div>
        </div>
        <Footerr />
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="bg-[#f9f8f4] min-h-screen flex flex-col">
      <TopBar />

      <div className="flex-1 flex justify-center items-center py-16 px-4">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl shadow-2xl border border-[#e2d1a6]/60 w-full max-w-3xl p-8 relative overflow-hidden"
        >
          {/* Decorative Accent */}
          <div className="absolute top-0 left-0 w-full h-[6px] bg-gradient-to-r from-[#b99653] via-[#e8c66a] to-[#b99653]" />

          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-4xl font-extrabold text-[#101c4c] tracking-tight">
              Secure Checkout
            </h1>
            <p className="text-gray-600 text-base mt-3">
              Complete your payment securely for{" "}
              <strong className="text-[#b99653]">{product.name}</strong>
            </p>
          </div>

          {/* Product Summary */}
          <div className="bg-[#fff9f1] border border-[#f2e1b3] rounded-2xl p-6 mb-8 flex flex-col md:flex-row items-center gap-5 shadow-inner">
            {/* Frame with domain name inside */}
            <div className="relative w-36 h-24 rounded-lg border border-[#d1b678]/50 shadow-sm overflow-hidden flex-shrink-0">
              <img
                src={frameBg}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <h2 className="text-[13px] font-bold text-[#000000] bg-white/70 px-2 py-[2px] text-center rounded">
                  {product.name}
                </h2>
              </div>
            </div>

            <div className="flex-1">
              <h3 className="font-semibold text-[#101c4c] text-lg mb-1 capitalize">
                {product.name}
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                {product.description ||
                  product.whyToBuyExplanation ||
                  "A premium .gold domain to empower your brand presence."}
              </p>
              <p className="text-sm text-gray-600">
                Registrar:{" "}
                <span className="font-semibold text-[#101c4c]">
                  {product.registrar || "Gold Registry Inc."}
                </span>
              </p>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-sm text-gray-700">
                  Category:{" "}
                  <span className="font-medium">
                    {product.category || "Gold Community"}
                  </span>
                </span>
                <span className="text-[#b99653] font-bold text-2xl">
                  ₹{(product.price || 0).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-8 shadow-sm">
            <p className="text-[#101c4c] text-sm font-medium mb-1">
              Logged in as:
            </p>
            <p className="text-[#101c4c]/90 text-sm">
              {loginUser?.username} ({loginUser?.email_id})
            </p>
            <p className="text-[#101c4c]/80 text-sm">
              📞 {loginUser?.mobile_number}
            </p>
          </div>

          {/* Secure Payment Badge */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 px-5 py-2 rounded-full text-sm text-green-700 font-medium shadow-sm">
              🔒 100% Secure Payment • Powered by Cashfree
            </div>
          </div>

          {/* CTA Button */}
          <motion.button
            whileTap={{ scale: 0.97 }}
            disabled={isPaying || !cashfree || isOwner}
            onClick={handlePayment}
            className={`w-full py-4 font-bold text-lg rounded-lg text-white tracking-wide shadow-md transition-all ${
              isPaying || !cashfree || isOwner
                ? "bg-gray-400 cursor-not-allowed"
                : "hover:scale-[1.02]"
            }`}
            style={{
              background:
                isPaying || !cashfree || isOwner
                  ? "#9ca3af"
                  : "linear-gradient(90deg, #b99653 0%, #e3b757 50%, #b99653 100%)",
            }}
          >
            {!cashfree ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                Loading Payment System...
              </div>
            ) : isPaying ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                Processing Payment...
              </div>
            ) : (
              `Pay ₹${(product.price || 0).toLocaleString()} & Confirm Order`
            )}
          </motion.button>

          {/* Back Button */}
          <button
            onClick={() => navigate("/marketplace")}
            disabled={isPaying}
            className="w-full mt-5 text-sm text-gray-500 hover:text-[#b99653] transition disabled:opacity-50"
          >
            ← Continue Exploring Marketplace
          </button>

          {/* Payment Logos */}
          <div className="w-full flex justify-center items-center mt-8">
            <img
              src={upi}
              alt="Payment Methods"
              className="max-w-[220px] md:max-w-[280px] lg:max-w-[320px] h-auto opacity-80 mx-auto"
            />
          </div>

          {/* Legal Footer */}
          <p className="text-xs text-gray-500 text-center mt-6">
            By proceeding, you agree to our{" "}
            <span
              className="text-[#b99653] cursor-pointer hover:underline"
              onClick={() => navigate("/terms-conditions")}
            >
              Terms & Conditions
            </span>{" "}
            and{" "}
            <span
              className="text-[#b99653] cursor-pointer hover:underline"
              onClick={() => navigate("/privacy-policy")}
            >
              Privacy Policy
            </span>
            .
          </p>
        </motion.div>
      </div>

      <Footerr />
    </div>
  );
}

export default MarketplaceCheckout;